/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Editor } from "@tiptap/react";
import React, { ReactText, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil";
import { updateEmail } from "../../../api/Email";
import {
  DelayTimeAtom,
  DelayTimeIsOverAtom,
  DelayTimeToEndAtom,
} from "../../../atoms/DelayTimeAtom";
import { EmailMode } from "../../../atoms/EmailAtom";
import { ScheduleDateAtom } from "../../../atoms/ScheduleDateAtom";
import { IInternalUser, InternalUserTypes } from "../../../atoms/UserAtom";
import { IEmailType, IUserIRoute } from "../../../generated/Api";
import useClickOutside from "../../../hook/useClickOutside";
import { useDefaultAlias } from "../../../hook/useDefaultAlias";
import useSetEmail from "../../../hook/useSetEmail";
import { getNewTime } from "../../../pages/new-email";
import { customTooltip } from "../../../styles/general";
import { IFooter } from "../../../types/UserTypes";
import { FormatEmail } from "../../../utils/FormatEmail";
import IconBerserkerMail from "../../Icon";
import StyledButton from "../../StyledButton";
import DelayBroadcastEmailToast from "../../Toasts/DelayBroadcastEmail";
import { useModal } from "../Modal";

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  width: 1000px;

  @media (max-width: 850px) {
    width: 100vw;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  background-color: #f4f4f4;
  height: fit-content;
  max-height: calc(70vh + 85px);
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  padding: 32px 24px;
  border-radius: 0px 0px 10px 10px;
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eceeee;
  padding: 16px 20px;
`;

const Title = styled.div`
  color: #2f2f2f;
  font-weight: bold;
  width: 100%;
  margin-bottom: 12px;
  font-size: 24px;
`;

const CloseIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 8px 22px;
  z-index: 15px;
  cursor: pointer;
  border: 1px solid #dddddd;
  border-radius: 5px;
`;

const HeaderDescription = styled.div`
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #2f2f2f;
  margin-right: 10px;
`;

const StopwatchIconContainer = styled.div`
  margin-right: 11px;
`;

const TimerContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  margin-right: 15px;
  color: #fb972e;
`;

const TimerValue = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border: 1px solid #fb972e;
  background: #fff2e5;
  border-radius: 4px;
`;

const Separator = styled.div`
  margin: 0px 5px;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const SendFromOnTop = styled.div`
  margin-right: auto;
  margin-top: -15px;
  padding-bottom: 15px;
`;

const StyledLoading = styled.div`
  border: 3px solid #facb99;
  border-top: 3px solid #fb972e;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
  margin-right: 11.5px;
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

interface Props {
  subject: string;
  body: string;
  user: IUserIRoute;
  internalUser: IInternalUser;
  emailInfo: {
    footer?: IFooter;
    footerId?: string;
    defaultModeColorLink?: string;
  };
  emailId: string;
  editor: Editor;
  mode: EmailMode;
}

const EXTRA_DELAY_PREPARE_TO_SEND = 3;

export default function PreviewEmailWithTimer(
  props: Props
): JSX.Element | null {
  const {
    body,
    subject,
    user,
    internalUser,
    emailInfo,
    emailId,
    editor,
    mode,
  } = props;
  const modal = useModal();
  const style = body.split('pre style="')[1]?.split('"')[0] || null;
  const { linkedBody, footer } = FormatEmail.format(body, user, emailInfo);
  const delayTime = useRecoilValue(DelayTimeAtom);
  const [delayTimeToEnd, setDelayTimeToEnd] =
    useRecoilState(DelayTimeToEndAtom);
  const [delayTimeIsOver, setDelayTimeIsOver] =
    useRecoilState(DelayTimeIsOverAtom);
  const [prepareToSend, setPrepareToSend] = useState(false);
  const resetScheduleDate = useResetRecoilState(ScheduleDateAtom);
  const { resetEmailContent } = useSetEmail(mode, editor);
  const [isClose, setIsClose] = useState(false);

  const { defaultAlias } = useDefaultAlias({
    // Not an issue because the user used here is a subset
    // of the whole user, and this one on PreviewEmailWithTimer has all
    // the required values (`user.emailOptions.aliases`)
    // @ts-ignore
    user,
    internalUser,
  });

  const containerRef = useClickOutside(() => {
    setIsClose(true);
  });

  useEffect(() => {
    if (!isClose) return;

    !delayTimeIsOver && openCountdownToast();

    modal.popModal();
  }, [isClose, delayTimeIsOver]);

  useEffect(() => {
    const interval = setInterval(() => {
      const time = getNewTime(delayTime);
      setDelayTimeToEnd(time);
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!delayTimeToEnd) return;
    if (delayTimeToEnd.min === 0 && delayTimeToEnd.sec <= 0) {
      setPrepareToSend(true);
    }

    if (
      delayTimeToEnd.min === 0 &&
      delayTimeToEnd.sec <= 0 - EXTRA_DELAY_PREPARE_TO_SEND
    ) {
      setPrepareToSend(false);
      setDelayTimeIsOver(true);

      resetEmailContent.current();
      resetScheduleDate();
    }
  }, [delayTimeToEnd]);

  function openPreviewEmailWithTimer() {
    modal.pushModal(
      <PreviewEmailWithTimer
        subject={subject}
        body={body}
        user={user}
        emailInfo={emailInfo}
        emailId={emailId}
        editor={editor}
        mode={mode}
        internalUser={internalUser}
      />,
      "",
      true
    );
    toast.dismiss();
  }

  const openCountdownToast = () =>
    toast(
      <DelayBroadcastEmailToast
        editor={editor}
        mode={mode}
        viewEmail={openPreviewEmailWithTimer}
        cancelEmail={async () => {
          const { count } = await updateEmail(emailId, {
            type: IEmailType.Cancelled,
          });

          if (count) {
            toast("Email cancelled successfully", { type: "success" });
            toast.dismiss("delayEmailToastId");
          }
        }}
      />,
      {
        toastId: "delayEmailToastId",
        position: "bottom-right",
        autoClose:
          60000 * delayTimeToEnd.min + 1000 * delayTimeToEnd.sec + 5000,
        closeButton: false,
        hideProgressBar: true,
        closeOnClick: false,
        style: {
          backgroundColor: "#333333",
          boxShadow: "0px 3px 8px #0000004d",
          padding: 0,
          cursor: "auto",
          width: "330px",
        },
      }
    );

  return (
    <ModalBody ref={containerRef}>
      <Header>
        <HeaderContent>
          <HeaderDescription>
            {!prepareToSend && !delayTimeIsOver && (
              <>
                <StopwatchIconContainer>
                  <IconBerserkerMail name="stopwatch" />
                </StopwatchIconContainer>
                This email broadcast will be sent in
              </>
            )}
            {delayTimeIsOver &&
              !prepareToSend &&
              "This email broadcast was sent"}

            {prepareToSend && (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                `}
              >
                <StyledLoading />
                <span>Preparing to send…</span>
              </div>
            )}
          </HeaderDescription>
          {!delayTimeIsOver ? (
            <>
              {!prepareToSend && (
                <TimerContainer>
                  <TimerValue>
                    {delayTimeToEnd?.min.toLocaleString("en-US", {
                      minimumIntegerDigits: 2,
                    })}
                  </TimerValue>
                  <Separator>:</Separator>
                  <TimerValue>
                    {delayTimeToEnd?.sec.toLocaleString("en-US", {
                      minimumIntegerDigits: 2,
                    })}
                  </TimerValue>
                </TimerContainer>
              )}
              <StyledButton
                css={css`
                  height: 40px;
                  background-color: #fff;
                  color: #555555;
                  border: 1px solid #dddddd;
                  border-radius: 5px;
                  padding: 13px 8px;
                `}
                onClick={async () => {
                  const { count } = await updateEmail(emailId, {
                    type: IEmailType.Cancelled,
                  });

                  if (count) {
                    toast("Email cancelled successfully", { type: "success" });
                    modal.popModal();
                  }
                }}
              >
                Cancel Broadcast
              </StyledButton>
            </>
          ) : (
            <IconBerserkerMail
              name="check"
              width={20}
              height={20}
              color="#75B85C"
            />
          )}
        </HeaderContent>
        <div data-tip data-for="hidePreview">
          <ReactTooltip
            id="hidePreview"
            place="top"
            effect="solid"
            globalEventOff="click"
            backgroundColor="#333333"
            css={customTooltip}
          >
            <TooltipText>Hide Preview</TooltipText>
          </ReactTooltip>
          <StyledButton
            onClick={() => {
              setIsClose(true);
            }}
            inverted
          >
            Hide
          </StyledButton>
        </div>
      </Header>
      <Wrapper>
        <SendFromOnTop>
          Send From: {`${defaultAlias.fromName} <${defaultAlias.address}>`}
        </SendFromOnTop>
        {subject && subject.trim() !== "" && <Title>{subject}</Title>}

        <div
          css={css`
            pre:first-child {
              ${style};
            }

            width: 100%;
            * {
              max-width: ${user?.emailOptions.maxWidth
                ? `${user?.emailOptions.maxWidth}px`
                : "100%"};
              font-size: medium;
              box-sizing: content-box;
              line-height: 1.5em;
              letter-spacing: 0.025em;
            }
          `}
        >
          <div
            css={css`
              background: transparent;
            `}
            dangerouslySetInnerHTML={{
              __html:
                `${linkedBody}\n` +
                `<pre style="overflow-wrap: break-word; white-space: pre-wrap; width:100%;${
                  style ? style : "font: small/ 1.5  Arial,Helvetica,sans-serif"
                }; font-size: small; 
                  ">${footer}</pre>`,
            }}
          ></div>
        </div>
      </Wrapper>
    </ModalBody>
  );
}
